import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  TextField,
  Button,
  FormHelperText,
} from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { useNavigate, useParams } from "react-router-dom";
import { get, put } from "../../../utils/api";
import { numberError, textError } from "../../../utils/validationMessage";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import GovernanceLayout from "../../../Layout/GovernanceLayout";
import {
  formatNumberInputWithCommas,
  stripNumberFormatting,
} from "../../../utils/common";
// import {id} from "../../../utils/companyId";

const BoardStructureForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const memberFields = [
    "independentBoardMembers",
    "femaleBoardMembers",
    "minorityGroupBoardMembers",
  ];

  const [disabledMemberFields, setDisabledMemberFields] = useState(new Set());
  const [formData, setFormData] = useState({
    totalBoardMembers: "",
    independentBoardMembers: "",
    femaleBoardMembers: "",
    minorityGroupBoardMembers: "",
    totalTenure: "",
    numberOfBoard: "",
    averageTenure: "",
    totalTenureBoard: "",
    averageBoardTenure: "",
  });

  const [errors, setErrors] = useState({
    totalBoardMembers: "",
    independentBoardMembers: "",
    femaleBoardMembers: "",
    minorityGroupBoardMembers: "",
    totalTenure: "",
    numberOfBoard: "",
    totalTenureBoard: "",
    averageTenure: "",
    averageBoardTenure: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/governance/getGovernance`, {
          params: {
            id: id, // Pass the id to get the specific data
          },
        });

        // If data exists, populate the form with the fetched values
        const fetchedData = response.data.data[0]; // Assuming the API returns an array of governance data

        if (fetchedData) {
          setFormData({
            totalBoardMembers:
              fetchedData.boardStructure?.totalBoardMembers !== undefined &&
              fetchedData.boardStructure?.totalBoardMembers !== null &&
              fetchedData.boardStructure?.totalBoardMembers !== ""
                ? formatNumberInputWithCommas(
                    String(fetchedData.boardStructure?.totalBoardMembers),
                  )
                : "",
            independentBoardMembers:
              fetchedData.boardStructure?.independentBoardMembers !== undefined &&
              fetchedData.boardStructure?.independentBoardMembers !== null &&
              fetchedData.boardStructure?.independentBoardMembers !== ""
                ? formatNumberInputWithCommas(
                    String(fetchedData.boardStructure?.independentBoardMembers),
                  )
                : "",
            femaleBoardMembers:
              fetchedData.boardStructure?.femaleBoardMembers !== undefined &&
              fetchedData.boardStructure?.femaleBoardMembers !== null &&
              fetchedData.boardStructure?.femaleBoardMembers !== ""
                ? formatNumberInputWithCommas(
                    String(fetchedData.boardStructure?.femaleBoardMembers),
                  )
                : "",
            minorityGroupBoardMembers:
              fetchedData.boardStructure?.minorityGroupBoardMembers !==
                undefined &&
              fetchedData.boardStructure?.minorityGroupBoardMembers !== null &&
              fetchedData.boardStructure?.minorityGroupBoardMembers !== ""
                ? formatNumberInputWithCommas(
                    String(fetchedData.boardStructure?.minorityGroupBoardMembers),
                  )
                : "",
            totalTenure:
              fetchedData.boardStructure?.totalTenure !== undefined &&
              fetchedData.boardStructure?.totalTenure !== null &&
              fetchedData.boardStructure?.totalTenure !== ""
                ? formatNumberInputWithCommas(
                    String(fetchedData.boardStructure?.totalTenure),
                  )
                : "",
            numberOfBoard:
              fetchedData.boardStructure?.numberOfBoard !== undefined &&
              fetchedData.boardStructure?.numberOfBoard !== null &&
              fetchedData.boardStructure?.numberOfBoard !== ""
                ? formatNumberInputWithCommas(
                    String(fetchedData.boardStructure?.numberOfBoard),
                  )
                : "",
            averageTenure:
              fetchedData.boardStructure?.averageTenure !== undefined &&
              fetchedData.boardStructure?.averageTenure !== null &&
              fetchedData.boardStructure?.averageTenure !== ""
                ? formatNumberInputWithCommas(
                    String(fetchedData.boardStructure?.averageTenure),
                  )
                : "",
            totalTenureBoard:
              fetchedData.boardStructure?.totalTenureBoard !== undefined &&
              fetchedData.boardStructure?.totalTenureBoard !== null &&
              fetchedData.boardStructure?.totalTenureBoard !== ""
                ? formatNumberInputWithCommas(
                    String(fetchedData.boardStructure?.totalTenureBoard),
                  )
                : "",
            averageBoardTenure:
              fetchedData.boardStructure?.averageBoardTenure !== undefined &&
              fetchedData.boardStructure?.averageBoardTenure !== null &&
              fetchedData.boardStructure?.averageBoardTenure !== ""
                ? formatNumberInputWithCommas(
                    String(fetchedData.boardStructure?.averageBoardTenure),
                  )
                : "",
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const fields = [
    {
      label: "Total Number of Board Members",
      field: "totalBoardMembers",
      placeholder: "Enter Number",
      col: 12,
    },
    {
      label: "Number of Independent Board Members",
      field: "independentBoardMembers",
      placeholder: "Enter Number",
      col: 12,
    },
    {
      label: "Number of Female Board Members",
      field: "femaleBoardMembers",
      placeholder: "Enter Number",
      col: 12,
    },
    {
      label: "Number of Board Members from Minority Groups",
      field: "minorityGroupBoardMembers",
      placeholder: "Enter Number",
      col: 12,
    },
    {
      label: "Total tenure of all board member who left (in years)",
      field: "totalTenure",
      placeholder: "Enter Number",
      col: 12,
    },
    {
      label: "Number of board members who left",
      field: "numberOfBoard",
      placeholder: "Enter Number",
      col: 12,
    },
    {
      label: "Average Tenure of Exiting Board members (in years)",
      field: "averageTenure",
      placeholder: "Auto calculate",
      col: 12,
    },
    {
      label: "Total tenure of all board members (in years)",
      field: "totalTenureBoard",
      placeholder: "Enter Number",
      col: 12,
    },
    {
      label: "Average Tenure of Board Members (in years)",
      field: "averageBoardTenure",
      placeholder: "Auto calculate",
      col: 12,
    },
  ];

  useEffect(() => {
    const totalTenureBoard = toNum(formData.totalTenureBoard);
    const totalBoardMembers = toNum(formData.totalBoardMembers);

    const totalTenure = toNum(formData.totalTenure);
    const numberOfBoard = toNum(formData.numberOfBoard);

    const isValid = (a, b) => !isNaN(a) && !isNaN(b);

    if (isValid(totalTenureBoard, totalBoardMembers)) {
      const average =
        totalBoardMembers === 0
          ? 0
          : (totalTenureBoard / totalBoardMembers).toFixed(2);
      setFormData((prev) => ({
        ...prev,
        averageBoardTenure: average,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        averageBoardTenure: "",
      }));
    }

    // Point 7: Average tenure of exiting board members = Point 5 / Point 6
    if (isValid(totalTenure, numberOfBoard)) {
      const average =
        numberOfBoard === 0 ? 0 : (totalTenure / numberOfBoard).toFixed(2);
      setFormData((prev) => ({
        ...prev,
        averageTenure: average,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        averageTenure: "",
      }));
    }
  }, [
    formData.totalTenureBoard,
    formData.totalBoardMembers,
    formData.totalTenure,
    formData.numberOfBoard,
  ]);

  const isNumericInput = (value) => {
    const raw = stripNumberFormatting(value);
    return /^\d*\.?\d*$/.test(raw); // Allows numbers and decimal point (ignores commas)
  };

  const toNum = (v) => {
    const n = parseFloat(stripNumberFormatting(v));
    return Number.isFinite(n) ? n : 0;
  };

  const validateBusinessRules = (data, baseErrors = {}) => {
    const newErrors = { ...baseErrors };
    let hasErrors = false;
    const total = toNum(data.totalBoardMembers);

    const dependentFields = [
      { field: "independentBoardMembers", label: "Independent Board Members" },
      { field: "femaleBoardMembers", label: "Female Board Members" },
      { field: "minorityGroupBoardMembers", label: "Minority Group Board Members" },
      { field: "numberOfBoard", label: "Number of board members who left" },
    ];

    dependentFields.forEach(({ field, label }) => {
      const value = toNum(data[field]);
      if (value > total) {
        // Explicit message for numberOfBoard as requested
        newErrors[field] =
          field === "numberOfBoard"
            ? "Number of board members who left cannot be greater than Total Number of Board Members"
            : `${label} cannot exceed Total Board Members`;
        hasErrors = true;
      } else {
        newErrors[field] = "";  // Clear error if validation passes
      }
    });

    // Sum of member categories cannot exceed total board members
    const sumMembers = memberFields.reduce(
      (acc, f) => acc + toNum(data[f]),
      0
    );
    if (sumMembers > total) {
      // Put a general error on total as well (optional) and keep field-level errors handled elsewhere
      newErrors.totalBoardMembers =
        "Sum of Independent + Female + Minority cannot exceed Total Board Members";
      hasErrors = true;
    } else {
      // Only clear if it was set by this rule
      if (
        newErrors.totalBoardMembers ===
          "Sum of Independent + Female + Minority cannot exceed Total Board Members" ||
        newErrors.totalBoardMembers ===
          "Total Board Members cannot be less than Independent + Female + Minority" ||
        (String(data.totalBoardMembers || "").trim() !== "" &&
          newErrors.totalBoardMembers === textError)
      ) {
        newErrors.totalBoardMembers = "";
      }
    }

    return { newErrors, hasErrors };
  };

  // Updated handleChange function
  const handleChange = (field, value) => {
    const formatted = formatNumberInputWithCommas(value);
    // Only update if value is empty or numeric
    if (formatted === "" || isNumericInput(formatted)) {
      // Update form data
      const updatedFormData = { ...formData, [field]: formatted };
      setFormData(updatedFormData);

      // Clear previous error
      setErrors((prev) => ({ ...prev, [field]: "" }));

      // Set error if field is empty (except for averageBoardTenure)
      if (field !== "averageBoardTenure" && formatted === "") {
        setErrors((prev) => ({
          ...prev,
          [field]: "Please fill this field.",
        }));
      }

      // Field-level validations for member categories vs total
      if (memberFields.includes(field) || field === "totalBoardMembers") {
        const total = toNum(updatedFormData.totalBoardMembers);
        const currentVal = toNum(updatedFormData[field]);

        // If editing one of the member fields, check it doesn't exceed total
        if (memberFields.includes(field) && total > 0 && currentVal > total) {
          setErrors((prev) => ({
            ...prev,
            [field]: "Value cannot exceed Total Board Members",
          }));
        }

        // Check sum constraint; show error on the currently edited member field
        const sumMembers = memberFields.reduce(
          (acc, f) => acc + toNum(updatedFormData[f]),
          0
        );
        if (total > 0 && sumMembers > total) {
          // show error on the field being edited (member field)
          if (memberFields.includes(field)) {
            setErrors((prev) => ({
              ...prev,
              [field]:
                "Sum of Independent + Female + Minority cannot exceed Total Board Members",
            }));
          } else {
            // total was edited and became smaller than sum
            setErrors((prev) => ({
              ...prev,
              totalBoardMembers:
                "Total Board Members cannot be less than Independent + Female + Minority",
            }));
          }
        }

        // If sum matches total, set any empty member fields to "0" and disable them
        if (total > 0 && sumMembers === total) {
          const nextDisabled = new Set();
          const nextData = { ...updatedFormData };
          memberFields.forEach((f) => {
            if (nextData[f] === "") {
              nextData[f] = "0";
              nextDisabled.add(f);
            } else if (String(nextData[f]) === "0") {
              // If explicitly 0, also disable per requirement
              nextDisabled.add(f);
            }
          });
          setFormData(nextData);
          setDisabledMemberFields(nextDisabled);
        } else {
          // If not matched, enable member fields
          setDisabledMemberFields(new Set());
        }
      }

      // Validate business rules when relevant fields change
      if (
        field === "totalBoardMembers" ||
        [
          "independentBoardMembers",
          "femaleBoardMembers",
          "minorityGroupBoardMembers",
          "numberOfBoard",
        ].includes(field)
      ) {
        const { newErrors } = validateBusinessRules(updatedFormData, {
          ...errors,
          [field]:
            field !== "averageBoardTenure" && formatted === ""
              ? textError
              : "",
        });
        setErrors(newErrors);
      }
    }
  };

  const handleKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, decimal point
    if (
      [46, 8, 9, 27, 13, 110, 190].includes(e.keyCode) ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true) ||
      // Allow: numbers on numeric keypad
      (e.keyCode >= 96 && e.keyCode <= 105)
    ) {
      return;
    }

    // Prevent non-numeric keys
    if (
      (e.keyCode < 48 || e.keyCode > 57) &&
      (e.keyCode < 96 || e.keyCode > 105)
    ) {
      e.preventDefault();
    }
  };

  const Move = async () => {
    // Check for empty fields

    const nonValidatedFields = ["averageBoardTenure", "averageTenure"];
    const newErrors = {};
    let hasErrors = false;

    Object.keys(formData).forEach((key) => {
      // if (!formData[key]) {
      if (!nonValidatedFields.includes(key) && formData[key] === "") {
        newErrors[key] = textError;
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

      // Business rule validation
      const { newErrors: businessErrors, hasErrors: hasBusinessErrors } =
      validateBusinessRules(formData);
      
    if (hasBusinessErrors) {
      setErrors(businessErrors);
      return;
    }

    try {
      const cleanedBoardStructure = Object.fromEntries(
        Object.entries(formData).map(([k, v]) => [
          k,
          v === "" ? "" : stripNumberFormatting(v),
        ]),
      );
      const response = await put(`/governance/updateGovernance`, {
        id: id,
        boardStructure: cleanedBoardStructure,
        module: "quarterly",
        stepsCompleted: { boardStructure: true },
      });
      console.log("Data saved successfully:", response.data);
      
      // Do NOT use module in URL. Determine next step from backend record.
      let nextModule = "risk-management";
      try {
        const moduleRes = await get(`/governance/getGovernance`, {module: "quarterly"});
        const record = moduleRes?.data?.data?.[0];
        const rawModule = (record?.module || record?.entryType || "")
          .toLowerCase()
          .trim();
        if (rawModule.includes("annual")) {
          nextModule = "risk-management";
        }
      } catch (e) {
        // fallback stays risk-management
        console.error("Failed to read governance module for navigation", e);
      }
      navigate(`/governance/data/${nextModule}/${id}`);
    } catch (error) {
      console.error("Error saving data:", error.response || error);
    }
  };

  return (
    <MainDashboardLayout sidebar={<GovernanceLayout />}>
      <Box
        sx={{
          width: "100%",
          margin: "20px auto",
          padding: "20px",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start", // Align content to the left
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000",
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000",
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#1976d2",
          },
        }}
      >

        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{
            fontWeight: "bolder",
            margin: "1.2% 0",
            textDecoration: "underline",
          }}
        >
          A. BOARD STRUCTURE AND COMPOSITION
        </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              columnGap: "24px",
              rowGap: "16px",
              width: "100%",
              maxWidth: "900px",
            }}
          >
            {fields.map((field, index) => (
              <Box
                key={index}
                sx={{
                  // col: 12 → full row, col: 6 → fixed half width
                  flex:
                    field.col === 12
                      ? "1 1 100%"
                      : "0 0 calc(50% - 24px)",
                  minWidth: field.col === 12 ? "100%" : "360px",
                }}
              >
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  {`${index + 1}.`}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: "bold", margin: "1.2% 0" }}
                >
                  {field.label}
                </Typography>
              </Box>
              <TextField
                value={formData[field.field]}
                onChange={(e) => handleChange(field.field, e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={field.placeholder}
                disabled={
                  field.field === "averageBoardTenure" ||
                  field.field === "averageTenure" ||
                  disabledMemberFields.has(field.field)
                }
                variant="outlined"
                
                error={
                  !!errors[field.field] &&
                  field.field !== "averageBoardTenure" &&
                  field.field !== "averageTenure"
                }
                sx={{
                  width: "70%",
                  "& .MuiInputBase-root": {
                    height: 40, // Reduce the text field height
                  },
                  ml: "1.1rem",
                }}
              />
              {errors[field.field] && (
                <FormHelperText
                  sx={{
                    color: "#D34040",
                    fontSize: "14px",
                    marginLeft: "1.1rem",
                  }}
                >
                  {errors[field.field]}{" "}
                  {/* Customize the error text appearance */}
                </FormHelperText>
              )}
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            width: "100%",
            mt: "10%",
            mb: 5,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
         
          <Button
            variant="contained"
            sx={{ bgcolor: "#1C486B", textTransform: "none" }}
            onClick={Move}
          >
            Save & Continue <EastIcon sx={{ ml: 2 }} />
          </Button>
        </Box>
      </Box>
    </MainDashboardLayout>
  );
};

export default BoardStructureForm;
